import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Braces, Check, Copy, GitBranch, HardDrive, KeyRound, Link2, RefreshCw, Server, X } from 'lucide-react'
import { generateHeaderSnippet, generateLinuxInstallCommand, generateMcpSnippet, type LinuxArch } from '../../engine/mcp/crypto'
import type { McpConfig, McpConnectionEntry, McpWorkspaceConfig } from '../../engine/mcp/types'
import { useMcpConnections } from '../../hooks/useMcpConnections'
import { decodeWorkspaceSecretV2 } from '@secureblob/sdk/workspaceSecretV2'
import { deriveMcpSeedIdentity } from '../../engine/mcp/credentialIdentity'
import './CreateMcpConnectionModal.css'

interface ViewMcpConfigModalProps {
    isOpen: boolean
    onClose: () => void
    connection: McpConnectionEntry
    onOpenConnection?: (configBlobId: string) => boolean | void
}

type McpClient = 'cursor' | 'antigravity' | 'header' | 'linux'
type ModalTab = 'setup' | 'graph' | 'credentials' | 'raw'

type LiveConfigState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'loaded'; config: McpConfig }
    | { status: 'error'; message: string }

interface LiveWorkspaceSummary {
    workspace: McpWorkspaceConfig
    alias: string
    registryKnown: boolean
    readerSeedPresent: boolean
    writerSeedPresent: boolean
    readKeyPresent: boolean
}

interface ChildCredentialKids {
    readerKid?: string
    writerKid?: string
}

function uniqueStrings(values: Array<string | undefined | null>): string[] {
    return [...new Set(values.filter((value): value is string => typeof value === 'string' && value.length > 0))]
}

function shortKid(kid: string): string {
    return kid.length > 14 ? `${kid.slice(0, 14)}...` : kid
}

function summarizeWorkspace(workspace: McpWorkspaceConfig, connection: McpConnectionEntry): LiveWorkspaceSummary {
    const secret = decodeWorkspaceSecretV2(workspace.workspaceSecret)
    return {
        workspace,
        alias: workspace.alias || workspace.blobId,
        registryKnown: connection.workspaceBlobIds.includes(workspace.blobId),
        readerSeedPresent: Boolean(secret?.readerSeed),
        writerSeedPresent: Boolean(secret?.writerSeed),
        readKeyPresent: Boolean(secret?.readKey),
    }
}

function errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error)
}

export default function ViewMcpConfigModal({ isOpen, onClose, connection, onOpenConnection }: ViewMcpConfigModalProps) {
    const { getToken, connections, update, readLiveConfig, importWorkspaceFromConfig } = useMcpConnections()
    const [mcpClient, setMcpClient] = useState<McpClient>('cursor')
    const [activeTab, setActiveTab] = useState<ModalTab>('setup')
    const [linuxArch, setLinuxArch] = useState<LinuxArch>('x86_64')
    const [copiedField, setCopiedField] = useState<string | null>(null)
    const [liveConfigState, setLiveConfigState] = useState<LiveConfigState>({ status: 'idle' })
    const [credentialKidsByBlobId, setCredentialKidsByBlobId] = useState<Record<string, ChildCredentialKids>>({})
    const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Clean up pending copy-feedback timer on unmount
    useEffect(() => () => { if (copyTimerRef.current) clearTimeout(copyTimerRef.current) }, [])

    const connectionToken = useMemo(() => getToken(connection), [connection, getToken])

    const snippet = useMemo(() => {
        if (mcpClient === 'linux') {
            return generateLinuxInstallCommand(connection, connection.label || 'secureblob', linuxArch)
        }
        if (mcpClient === 'header') {
            return generateHeaderSnippet(connection.configBlobId, connectionToken)
        }
        return generateMcpSnippet(connection, mcpClient)
    }, [connection, connectionToken, mcpClient, linuxArch])

    const refreshLiveConfig = useCallback(async () => {
        setLiveConfigState({ status: 'loading' })
        try {
            const config = await readLiveConfig(connection)
            setLiveConfigState({ status: 'loaded', config })
        } catch (error) {
            setLiveConfigState({ status: 'error', message: errorMessage(error) })
        }
    }, [connection, readLiveConfig])

    useEffect(() => {
        setLiveConfigState({ status: 'idle' })
    }, [connection.configBlobId])

    useEffect(() => {
        if (!isOpen) return
        if (activeTab !== 'graph' && activeTab !== 'credentials') return
        if (liveConfigState.status !== 'idle') return
        void refreshLiveConfig()
    }, [activeTab, isOpen, liveConfigState.status, refreshLiveConfig])

    const copyToClipboard = (text: string, field: string) => {
        navigator.clipboard.writeText(text)
        setCopiedField(field)
        if (copyTimerRef.current) clearTimeout(copyTimerRef.current)
        copyTimerRef.current = setTimeout(() => setCopiedField(null), 2000)
    }

    const linkedConfigEntries = useMemo(
        () => new Map((connections ?? []).map(entry => [entry.configBlobId, entry])),
        [connections],
    )
    const liveConfig = liveConfigState.status === 'loaded' ? liveConfigState.config : null
    const liveLinks = liveConfig?.links
    useEffect(() => {
        let cancelled = false
        if (!liveConfig) {
            setCredentialKidsByBlobId({})
            return () => { cancelled = true }
        }

        void Promise.all(liveConfig.workspaces.map(async workspace => {
            const secret = decodeWorkspaceSecretV2(workspace.workspaceSecret)
            const reader = secret?.readerSeed
                ? await deriveMcpSeedIdentity(secret.readerSeed).catch(() => null)
                : null
            const writer = secret?.writerSeed
                ? await deriveMcpSeedIdentity(secret.writerSeed).catch(() => null)
                : null
            return [
                workspace.blobId,
                {
                    ...(reader ? { readerKid: reader.kid } : {}),
                    ...(writer ? { writerKid: writer.kid } : {}),
                },
            ] as const
        })).then(entries => {
            if (!cancelled) setCredentialKidsByBlobId(Object.fromEntries(entries))
        })

        return () => { cancelled = true }
    }, [liveConfig])
    const purpose = liveLinks?.purpose ?? connection.purpose ?? 'user-mcp'
    const linkedConfigBlobIds = uniqueStrings([
        ...(connection.linkedConfigBlobIds ?? []),
        ...(liveLinks?.linkedConfigBlobIds ?? []),
    ])
    const liveWorkspaceSummaries = useMemo(
        () => liveConfig?.workspaces.map(workspace => summarizeWorkspace(workspace, connection)) ?? [],
        [connection, liveConfig],
    )
    const linkedWorkspaceRefs = uniqueStrings([
        ...((connection.linkedWorkspaceRefs ?? connection.workspaceBlobIds) as string[]),
        ...(liveLinks?.linkedWorkspaceRefs ?? []),
        ...liveWorkspaceSummaries.map(summary => summary.alias),
    ])
    const readerSeedCount = Object.keys(connection.mcpReaderSeeds ?? {}).length
    const writerSeedCount = Object.keys(connection.mcpWriterSeeds ?? {}).length
    const liveWorkspaceBlobIds = liveConfig?.workspaces.map(workspace => workspace.blobId) ?? []
    const hasLiveRegistryDrift = liveConfig
        ? liveWorkspaceBlobIds.some(blobId => !connection.workspaceBlobIds.includes(blobId))
            || (liveLinks?.linkedWorkspaceRefs ?? []).some(ref => !(connection.linkedWorkspaceRefs ?? []).includes(ref))
            || (liveLinks?.linkedConfigBlobIds ?? []).some(configBlobId => !(connection.linkedConfigBlobIds ?? []).includes(configBlobId))
            || Boolean(liveLinks?.purpose && liveLinks.purpose !== connection.purpose)
            || Boolean(liveLinks?.parentConfigBlobId && liveLinks.parentConfigBlobId !== connection.parentConfigBlobId)
        : false
    const handleSyncRegistryFromLive = useCallback(() => {
        if (!liveConfig) return
        update(connection.configBlobId, {
            workspaceBlobIds: liveConfig.workspaces.map(workspace => workspace.blobId),
            ...(liveLinks?.purpose ? { purpose: liveLinks.purpose } : {}),
            ...(liveLinks?.parentConfigBlobId ? { parentConfigBlobId: liveLinks.parentConfigBlobId } : {}),
            linkedConfigBlobIds: liveLinks?.linkedConfigBlobIds ?? [],
            linkedWorkspaceRefs: uniqueStrings([
                ...(liveLinks?.linkedWorkspaceRefs ?? []),
                ...liveConfig.workspaces.map(workspace => workspace.alias ?? workspace.blobId),
            ]),
        })
    }, [connection.configBlobId, liveConfig, liveLinks, update])
    const handleImportWorkspace = useCallback((workspace: McpWorkspaceConfig) => {
        importWorkspaceFromConfig(connection, workspace)
        if (liveConfig) {
            update(connection.configBlobId, {
                workspaceBlobIds: uniqueStrings([
                    ...connection.workspaceBlobIds,
                    workspace.blobId,
                ]),
            })
        }
    }, [connection, importWorkspaceFromConfig, liveConfig, update])
    const redactedConnectionJson = JSON.stringify({
        configBlobId: connection.configBlobId,
        label: connection.label,
        ownerKid: connection.ownerKid,
        createdAt: connection.createdAt,
        workspaceBlobIds: connection.workspaceBlobIds,
        purpose,
        parentConfigBlobId: connection.parentConfigBlobId,
        linkedConfigBlobIds,
        linkedWorkspaceRefs,
        credentials: {
            readKey: '<redacted>',
            readerSeed: connection.readerSeed ? '<redacted>' : undefined,
            writerSeed: '<redacted>',
            mcpReaderSeedCount: readerSeedCount,
            mcpWriterSeedCount: writerSeedCount,
        },
        liveConfig: liveConfig ? {
            label: liveConfig.label,
            purpose,
            workspaceCount: liveConfig.workspaces.length,
            linkedConfigBlobIds,
            linkedWorkspaceRefs,
        } : undefined,
    }, null, 2)

    if (!isOpen) return null

    return createPortal(
        <div className="mcp-modal-overlay" onClick={onClose}>
            <div className="mcp-modal" onClick={e => e.stopPropagation()}>
                <div className="mcp-modal-header">
                    <div className="mcp-modal-title">
                        <Server size={17} style={{ marginRight: 8, opacity: 0.65, verticalAlign: '-3px' }} />
                        {connection.label} — Setup
                    </div>
                    <button className="mcp-modal-close" onClick={onClose} aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                <div className="mcp-modal-body">
                    <div className="mcp-client-toggle mcp-modal-tabs" role="tablist" aria-label="MCP config views">
                        <button className={`mcp-client-btn ${activeTab === 'setup' ? 'active' : ''}`} onClick={() => setActiveTab('setup')} role="tab" aria-selected={activeTab === 'setup'}><Server size={14} /> Setup</button>
                        <button className={`mcp-client-btn ${activeTab === 'graph' ? 'active' : ''}`} onClick={() => setActiveTab('graph')} role="tab" aria-selected={activeTab === 'graph'}><GitBranch size={14} /> Graph</button>
                        <button className={`mcp-client-btn ${activeTab === 'credentials' ? 'active' : ''}`} onClick={() => setActiveTab('credentials')} role="tab" aria-selected={activeTab === 'credentials'}><KeyRound size={14} /> Credentials</button>
                        <button className={`mcp-client-btn ${activeTab === 'raw' ? 'active' : ''}`} onClick={() => setActiveTab('raw')} role="tab" aria-selected={activeTab === 'raw'}><Braces size={14} /> Raw</button>
                    </div>

                    {activeTab === 'setup' && (
                        <>
                            <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, margin: '0 0 20px', lineHeight: 1.5 }}>
                                Copy the configuration below into your MCP client, or use the one-line Linux install command.
                            </p>

                            <div className="mcp-client-toggle">
                                <button className={`mcp-client-btn ${mcpClient === 'cursor' ? 'active' : ''}`} onClick={() => setMcpClient('cursor')}><span className="mcp-client-icon">⌘</span> Cursor</button>
                                <button className={`mcp-client-btn ${mcpClient === 'antigravity' ? 'active' : ''}`} onClick={() => setMcpClient('antigravity')}><span className="mcp-client-icon">◆</span> Antigravity</button>
                                <button className={`mcp-client-btn ${mcpClient === 'header' ? 'active' : ''}`} onClick={() => setMcpClient('header')}><span className="mcp-client-icon">#</span> MCP w header</button>
                                <button className={`mcp-client-btn ${mcpClient === 'linux' ? 'active' : ''}`} onClick={() => setMcpClient('linux')}><HardDrive size={14} /> Linux</button>
                            </div>

                            {mcpClient === 'linux' && (
                                <div className="mcp-client-toggle mcp-client-toggle-arch" style={{ marginTop: -10 }}>
                                    <button className={`mcp-client-btn ${linuxArch === 'x86_64' ? 'active' : ''}`} onClick={() => setLinuxArch('x86_64')}>x86_64</button>
                                    <button className={`mcp-client-btn ${linuxArch === 'arm64' ? 'active' : ''}`} onClick={() => setLinuxArch('arm64')}>arm64</button>
                                </div>
                            )}

                            <div className="mcp-form-group">
                                <label className="mcp-label">{mcpClient === 'linux' ? 'Linux one-line install command' : 'Configuration snippet'}</label>
                                <div className="mcp-result-block">
                                    {snippet}
                                    <button className={`mcp-copy-btn ${copiedField === 'snippet' ? 'copied' : ''}`} onClick={() => copyToClipboard(snippet, 'snippet')} title="Copy">
                                        {copiedField === 'snippet' ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy</>}
                                    </button>
                                </div>
                            </div>

                            {mcpClient === 'linux' && (
                                <div className="mcp-form-group" style={{ marginTop: -8 }}>
                                    <label className="mcp-label">Connection token</label>
                                    <div className="mcp-result-block">
                                        {connectionToken}
                                        <button className={`mcp-copy-btn ${copiedField === 'token' ? 'copied' : ''}`} onClick={() => copyToClipboard(connectionToken, 'token')} title="Copy token">
                                            {copiedField === 'token' ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy</>}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="mcp-guide">
                                <strong>How to install:</strong>
                                {mcpClient === 'linux' ? (
                                    <ol style={{ paddingLeft: 20, margin: '8px 0 0' }}>
                                        <li>Copy one-line command and run in terminal.</li>
                                        <li>Script downloads the latest binary (first run triggers server-side build ~1 min, subsequent runs use cache).</li>
                                        <li>FUSE mounts at <code>~/&lt;name&gt;</code> — files appear immediately. Ctrl-C to stop.</li>
                                    </ol>
                                ) : mcpClient === 'cursor' ? (
                                    <ol style={{ paddingLeft: 20, margin: '8px 0 0' }}>
                                        <li>Open <strong>Cursor</strong></li>
                                        <li>Go to <strong>Settings {'>'} Features {'>'} MCP</strong></li>
                                        <li>Click &quot;Add new MCP server&quot;</li>
                                        <li>Paste the configuration above</li>
                                        <li>Cursor will open a browser — sign in with your Passkey to approve</li>
                                    </ol>
                                ) : mcpClient === 'antigravity' ? (
                                    <ol style={{ paddingLeft: 20, margin: '8px 0 0' }}>
                                        <li>Open <strong>Antigravity</strong> settings</li>
                                        <li>Edit your <strong>mcp_config.json</strong></li>
                                        <li>Add the <code>secureblob</code> entry from above into <code>mcpServers</code></li>
                                        <li>Restart the MCP server connection</li>
                                        <li>Antigravity will open a browser — sign in with your Passkey to approve</li>
                                    </ol>
                                ) : (
                                    <ol style={{ paddingLeft: 20, margin: '8px 0 0' }}>
                                        <li>Open your MCP client config file</li>
                                        <li>Add the <code>secureblob</code> entry from above into <code>mcpServers</code></li>
                                        <li>Keep the <code>X-SecureBlob-Key</code> header secret private</li>
                                        <li>Reload or restart the MCP server connection</li>
                                    </ol>
                                )}
                            </div>
                        </>
                    )}

                    {activeTab === 'graph' && (
                        <div className="mcp-graph-panel">
                            <div className="mcp-live-toolbar">
                                <div className={`mcp-live-status ${liveConfigState.status}`}>
                                    {liveConfigState.status === 'loaded'
                                        ? `Live config loaded: ${liveConfigState.config.workspaces.length} workspace${liveConfigState.config.workspaces.length === 1 ? '' : 's'}`
                                        : liveConfigState.status === 'loading'
                                            ? 'Loading live config'
                                            : liveConfigState.status === 'error'
                                                ? `Live config unavailable: ${liveConfigState.message}`
                                                : 'Registry snapshot'}
                                </div>
                                <div className="mcp-live-actions">
                                    <button type="button" className="mcp-icon-text-btn" onClick={() => void refreshLiveConfig()} disabled={liveConfigState.status === 'loading'}>
                                        <RefreshCw size={13} />
                                        Refresh
                                    </button>
                                    {liveConfig && hasLiveRegistryDrift && (
                                        <button type="button" className="mcp-icon-text-btn" onClick={handleSyncRegistryFromLive}>
                                            <Link2 size={13} />
                                            Sync registry
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="mcp-graph-summary">
                                <div>
                                    <span className="mcp-label">Purpose</span>
                                    <span className="mcp-badge">{purpose}</span>
                                </div>
                                <div>
                                    <span className="mcp-label">Config</span>
                                    <code className="mcp-inline-code">{connection.configBlobId}</code>
                                </div>
                                <div>
                                    <span className="mcp-label">Source</span>
                                    <span className="mcp-badge">{liveConfig ? 'live config' : 'registry'}</span>
                                </div>
                                {(liveLinks?.parentConfigBlobId || connection.parentConfigBlobId) && (
                                    <div>
                                        <span className="mcp-label">Parent config</span>
                                        <code className="mcp-inline-code">{liveLinks?.parentConfigBlobId ?? connection.parentConfigBlobId}</code>
                                    </div>
                                )}
                            </div>

                            <div className="mcp-form-group">
                                <label className="mcp-label">Workspaces</label>
                                <div className="mcp-linked-list">
                                    {linkedWorkspaceRefs.map(ref => (
                                        <button
                                            key={ref}
                                            type="button"
                                            className={`mcp-linked-pill ${copiedField === `workspace:${ref}` ? 'copied' : ''}`}
                                            title="Copy workspace ref"
                                            aria-label={`Copy workspace ref ${ref}`}
                                            onClick={() => copyToClipboard(ref, `workspace:${ref}`)}
                                        >
                                            <HardDrive size={13} />
                                            <span>{copiedField === `workspace:${ref}` ? 'Copied' : ref}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {liveWorkspaceSummaries.length > 0 && (
                                <div className="mcp-form-group">
                                    <label className="mcp-label">Live workspaces</label>
                                    <div className="mcp-live-list">
                                        {liveWorkspaceSummaries.map(summary => (
                                            <div key={summary.workspace.blobId} className="mcp-live-row">
                                                <div className="mcp-live-main">
                                                    <HardDrive size={14} />
                                                    <div className="mcp-live-copy">
                                                        <strong>{summary.workspace.title || summary.alias}</strong>
                                                        <code>{summary.alias}</code>
                                                        <code>{summary.workspace.blobId}</code>
                                                    </div>
                                                </div>
                                                <div className="mcp-live-badges">
                                                    <span className="mcp-badge">{summary.workspace.permissions.write ? 'read/write' : 'read'}</span>
                                                    <span className={`mcp-badge ${summary.registryKnown ? '' : 'muted'}`}>{summary.registryKnown ? 'registry' : 'linked only'}</span>
                                                    {!summary.registryKnown && (
                                                        <button type="button" className="mcp-icon-text-btn" onClick={() => handleImportWorkspace(summary.workspace)}>
                                                            <Link2 size={13} />
                                                            Add
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {linkedConfigBlobIds.length > 0 && (
                                <div className="mcp-form-group">
                                    <label className="mcp-label">Linked configs</label>
                                    <div className="mcp-linked-list">
                                        {linkedConfigBlobIds.map(configBlobId => {
                                            const linkedEntry = linkedConfigEntries.get(configBlobId)
                                            const canOpen = Boolean(linkedEntry && onOpenConnection)
                                            return (
                                                <button
                                                    key={configBlobId}
                                                    type="button"
                                                    className={`mcp-linked-pill ${copiedField === `config:${configBlobId}` ? 'copied' : ''}`}
                                                    title={canOpen ? `Open ${linkedEntry!.label}` : 'Copy config blob id'}
                                                    aria-label={canOpen ? `Open linked config ${configBlobId}` : `Copy config blob id ${configBlobId}`}
                                                    onClick={() => {
                                                        if (canOpen && onOpenConnection?.(configBlobId) !== false) return
                                                        copyToClipboard(configBlobId, `config:${configBlobId}`)
                                                    }}
                                                >
                                                    <GitBranch size={13} />
                                                    <span>{copiedField === `config:${configBlobId}` ? 'Copied' : (linkedEntry?.label ?? configBlobId)}</span>
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'credentials' && (
                        <div className="mcp-credential-panel">
                            <div className="mcp-live-toolbar">
                                <div className={`mcp-live-status ${liveConfigState.status}`}>
                                    {liveConfigState.status === 'loaded'
                                        ? 'Live child credentials loaded'
                                        : liveConfigState.status === 'loading'
                                            ? 'Loading child credentials'
                                            : liveConfigState.status === 'error'
                                                ? `Live child credentials unavailable: ${liveConfigState.message}`
                                                : 'Registry credential summary'}
                                </div>
                                <button type="button" className="mcp-icon-text-btn" onClick={() => void refreshLiveConfig()} disabled={liveConfigState.status === 'loading'}>
                                    <RefreshCw size={13} />
                                    Refresh
                                </button>
                            </div>

                            <div className="mcp-graph-summary">
                                <div>
                                    <span className="mcp-label">Workspace grants</span>
                                    <span className="mcp-badge">{liveConfig?.workspaces.length ?? connection.workspaceBlobIds.length}</span>
                                </div>
                                <div>
                                    <span className="mcp-label">Registry readers</span>
                                    <span className="mcp-badge">{readerSeedCount}</span>
                                </div>
                                <div>
                                    <span className="mcp-label">Registry writers</span>
                                    <span className="mcp-badge">{writerSeedCount}</span>
                                </div>
                            </div>

                            {liveWorkspaceSummaries.length > 0 && (
                                <div className="mcp-form-group">
                                    <label className="mcp-label">Child credentials</label>
                                    <div className="mcp-live-list">
                                        {liveWorkspaceSummaries.map(summary => {
                                            const credentialKids = credentialKidsByBlobId[summary.workspace.blobId]
                                            return (
                                                <div key={summary.workspace.blobId} className="mcp-live-row">
                                                    <div className="mcp-live-main">
                                                        <KeyRound size={14} />
                                                        <div className="mcp-live-copy">
                                                            <strong>{summary.workspace.title || summary.alias}</strong>
                                                            <code>{summary.alias}</code>
                                                            <code>{summary.workspace.blobId}</code>
                                                        </div>
                                                    </div>
                                                    <div className="mcp-live-badges">
                                                        <span className={`mcp-badge ${summary.readKeyPresent ? '' : 'muted'}`}>read key</span>
                                                        <span className={`mcp-badge ${summary.readerSeedPresent ? '' : 'muted'}`}>reader seed</span>
                                                        <span className={`mcp-badge ${summary.writerSeedPresent ? '' : 'muted'}`}>writer seed</span>
                                                        <span className={`mcp-badge ${(connection.mcpReaderSeeds ?? {})[summary.workspace.blobId] ? '' : 'muted'}`}>registry reader</span>
                                                        <span className={`mcp-badge ${(connection.mcpWriterSeeds ?? {})[summary.workspace.blobId] ? '' : 'muted'}`}>registry writer</span>
                                                        {credentialKids?.readerKid && (
                                                            <button
                                                                type="button"
                                                                className="mcp-kid-pill"
                                                                title="Copy reader kid"
                                                                onClick={() => copyToClipboard(credentialKids.readerKid!, `readerKid:${summary.workspace.blobId}`)}
                                                            >
                                                                reader kid {copiedField === `readerKid:${summary.workspace.blobId}` ? 'Copied' : shortKid(credentialKids.readerKid)}
                                                            </button>
                                                        )}
                                                        {credentialKids?.writerKid && (
                                                            <button
                                                                type="button"
                                                                className="mcp-kid-pill"
                                                                title="Copy writer kid"
                                                                onClick={() => copyToClipboard(credentialKids.writerKid!, `writerKid:${summary.workspace.blobId}`)}
                                                            >
                                                                writer kid {copiedField === `writerKid:${summary.workspace.blobId}` ? 'Copied' : shortKid(credentialKids.writerKid)}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}

                            <label className="mcp-label">Connection token</label>
                            <div className="mcp-result-block">
                                Token hidden by default. Use copy for explicit handoff.
                                <button className={`mcp-copy-btn ${copiedField === 'token' ? 'copied' : ''}`} onClick={() => copyToClipboard(connectionToken, 'token')} title="Copy token">
                                    {copiedField === 'token' ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy</>}
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'raw' && (
                        <div className="mcp-credential-panel">
                            <label className="mcp-label">Redacted registry entry</label>
                            <div className="mcp-result-block mcp-result-block-pre">
                                {redactedConnectionJson}
                                <button className={`mcp-copy-btn ${copiedField === 'raw' ? 'copied' : ''}`} onClick={() => copyToClipboard(redactedConnectionJson, 'raw')} title="Copy redacted JSON">
                                    {copiedField === 'raw' ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy</>}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="mcp-modal-footer">
                    <button className="mcp-btn mcp-btn-primary" onClick={onClose}>Done</button>
                </div>
            </div>
        </div>,
        document.body,
    )
}
